/**
 * `<caos-usage-bar>` — one finite allowance, read as used-of-maximum.
 * (Catalog: Status & feedback › UsageBar — one finite allowance drawn as used-of-maximum, with automatic pressure thresholds.)
 *
 * Deliberately NOT `<caos-progress-bar>`, and the difference is semantic rather than
 * visual. A progress bar reports an OPERATION advancing, where full is the goal and
 * arriving there is success. A usage bar reports CONSUMPTION of something finite —
 * API calls this hour, storage, seats — where full is the failure and the whole point
 * of drawing it is to be warned before you arrive. The two look alike and mean
 * opposite things, so they cannot be the same component with a flag: every consumer
 * of the progress bar would inherit a colour ramp that reddens on success.
 *
 * That inversion is why the intent is computed rather than passed. `warn-at` and
 * `danger-at` (percentages, defaulting to 75 and 90) decide the fill colour from
 * `used` and `maximum`, so a screen of twenty limits cannot disagree with itself about
 * what "nearly out" looks like — which is exactly what happens when each caller picks
 * its own colour. An explicit `intent` still overrides, for the caller that genuinely
 * knows better.
 *
 * Over-limit is a real state, not an overflow bug: `used` above `maximum` clamps the
 * FILL at 100% while the numbers keep telling the truth, because a bar drawn past its
 * own track says nothing and a number that silently stops counting is worse.
 *
 * `role="meter"` with `aria-valuenow/min/max` and `aria-valuetext`, because this is a
 * measurement within a known range, not a task advancing — the ARIA distinction is the
 * same one the component exists to make. Styling seams: `::part(bar)`, `::part(header)`,
 * `::part(label)`, `::part(value)`, `::part(track)`, `::part(fill)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';

export type CaosUsageIntent = 'accent' | 'success' | 'warning' | 'danger';

/** Default percentage at which the fill turns warning / danger. */
export const USAGE_WARN_AT = 75;
export const USAGE_DANGER_AT = 90;

const sheet = makeSheet(`
  :host { display: block; }

  .bar { display: flex; flex-direction: column; gap: var(--caos-space-2); }

  .header { display: flex; align-items: baseline; justify-content: space-between; gap: var(--caos-space-3); }
  .header:not(:has(.label:not(:empty), .value:not(:empty))) { display: none; }

  .label {
    font-family: var(--caos-font);
    font-size: 12.5px;
    font-weight: 500;
    color: var(--caos-color-text);
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .value {
    font-family: var(--caos-font);
    font-size: 12.5px;
    font-variant-numeric: tabular-nums;
    color: var(--caos-color-text-muted);
    flex: none;
  }
  .value:empty { display: none; }

  .track {
    position: relative;
    height: 8px;
    border-radius: var(--caos-radius-pill);
    background: var(--caos-color-surface-2);
    overflow: hidden;
  }

  .fill {
    height: 100%;
    border-radius: inherit;
    background: var(--caos-color-accent);
    transition: width 160ms ease-out;
  }
  @media (prefers-reduced-motion: reduce) {
    .fill { transition: none; }
  }

  :host([intent="success"]) .fill { background: var(--caos-color-success); }
  :host([intent="warning"]) .fill { background: var(--caos-color-warning); }
  :host([intent="danger"])  .fill { background: var(--caos-color-danger); }

  /* Over the allowance: the fill is pinned full and carries the danger hue. */
  :host([over-limit]) .fill { background: var(--caos-color-danger); }
  :host([over-limit]) .value { color: var(--caos-color-danger); font-weight: 600; }
`);

/** Parse an attribute that must be a finite number; anything else reads as undefined. */
function num(raw: string | null): number | undefined {
  if (raw === null || raw.trim() === '') return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * The intent a given consumption implies, given the two thresholds. Exported because the
 * limits panel groups bars and needs the SAME answer for its own summary line — two
 * implementations of "is this one in trouble" is precisely how a panel comes to disagree
 * with the bars inside it.
 */
export function usageIntent(percent: number, warnAt = USAGE_WARN_AT, dangerAt = USAGE_DANGER_AT): CaosUsageIntent {
  if (percent >= dangerAt) return 'danger';
  if (percent >= warnAt) return 'warning';
  return 'accent';
}

export class CaosUsageBar extends CaosElement {
  static get observedAttributes(): string[] {
    return ['used', 'maximum', 'label', 'intent', 'warn-at', 'danger-at', 'hide-value', 'unit'];
  }

  private barEl: HTMLDivElement;
  private headerEl: HTMLDivElement;
  private labelEl: HTMLSpanElement;
  private valueEl: HTMLSpanElement;
  private trackEl: HTMLDivElement;
  private fillEl: HTMLDivElement;

  constructor() {
    super(sheet);

    this.barEl = document.createElement('div');
    this.barEl.className = 'bar';
    this.barEl.setAttribute('part', 'bar');

    this.headerEl = document.createElement('div');
    this.headerEl.className = 'header';
    this.headerEl.setAttribute('part', 'header');

    this.labelEl = document.createElement('span');
    this.labelEl.className = 'label';
    this.labelEl.setAttribute('part', 'label');

    this.valueEl = document.createElement('span');
    this.valueEl.className = 'value';
    this.valueEl.setAttribute('part', 'value');

    this.trackEl = document.createElement('div');
    this.trackEl.className = 'track';
    this.trackEl.setAttribute('part', 'track');

    this.fillEl = document.createElement('div');
    this.fillEl.className = 'fill';
    this.fillEl.setAttribute('part', 'fill');

    this.trackEl.append(this.fillEl);
    this.headerEl.append(this.labelEl, this.valueEl);
    this.barEl.append(this.headerEl, this.trackEl);
    this.root.append(this.barEl);
  }

  connectedCallback(): void {
    // A measurement within a known range, not a task advancing.
    if (!this.hasAttribute('role')) this.setAttribute('role', 'meter');
    this.sync();
  }

  attributeChangedCallback(): void {
    this.sync();
  }

  /** The percentage consumed, clamped to [0, 100] for DRAWING only. */
  get percent(): number {
    const used = num(this.getAttribute('used')) ?? 0;
    const maximum = num(this.getAttribute('maximum'));
    // No maximum, or a nonsensical one, means there is no allowance to be a fraction of.
    if (maximum === undefined || maximum <= 0) return 0;
    return Math.max(0, Math.min(100, (used / maximum) * 100));
  }

  private sync(): void {
    const used = num(this.getAttribute('used')) ?? 0;
    const maximum = num(this.getAttribute('maximum'));
    const unit = this.getAttribute('unit') ?? '';
    const percent = this.percent;

    this.labelEl.textContent = this.getAttribute('label') ?? '';

    // The numbers tell the truth even when the bar cannot: over-limit pins the fill and
    // keeps counting.
    const overLimit = maximum !== undefined && maximum > 0 && used > maximum;
    this.toggleAttribute('over-limit', overLimit);

    const suffix = unit ? ` ${unit}` : '';
    const valueText =
      maximum === undefined || maximum <= 0
        ? `${format(used)}${suffix}`
        : `${format(used)} of ${format(maximum)}${suffix}`;
    this.valueEl.textContent = this.hasAttribute('hide-value') ? '' : valueText;

    this.fillEl.style.width = `${percent}%`;

    // An explicit intent wins; otherwise the thresholds decide, so a screen of limits
    // cannot disagree with itself about what "nearly out" looks like.
    if (!this.hasAttribute('intent')) {
      const warnAt = num(this.getAttribute('warn-at')) ?? USAGE_WARN_AT;
      const dangerAt = num(this.getAttribute('danger-at')) ?? USAGE_DANGER_AT;
      const computed = usageIntent(percent, warnAt, dangerAt);
      // `accent` is the resting state and carries no attribute, matching the sheet's default.
      if (computed === 'accent') this.removeAttribute('data-intent');
      else this.setAttribute('data-intent', computed);
      this.applyComputedIntent(computed);
    } else {
      this.removeAttribute('data-intent');
      this.fillEl.style.removeProperty('background');
    }

    this.setAttribute('aria-valuenow', String(used));
    this.setAttribute('aria-valuemin', '0');
    if (maximum !== undefined && maximum > 0) this.setAttribute('aria-valuemax', String(maximum));
    else this.removeAttribute('aria-valuemax');
    this.setAttribute('aria-valuetext', valueText);
    const label = this.getAttribute('label');
    if (label) this.setAttribute('aria-label', label);
  }

  /**
   * The computed intent is applied as an inline fill colour rather than by writing `intent`
   * onto the host. Writing it would make a DERIVED value indistinguishable from one the
   * caller set — the next `sync()` would read its own output back as an explicit override
   * and the thresholds would stop being consulted the moment they first fired.
   */
  private applyComputedIntent(intent: CaosUsageIntent): void {
    const token =
      intent === 'danger' ? 'var(--caos-color-danger)' : intent === 'warning' ? 'var(--caos-color-warning)' : '';
    if (token) this.fillEl.style.background = token;
    else this.fillEl.style.removeProperty('background');
  }
}

/** Thousands separators, and nothing else — the value is a count, not a currency. */
function format(n: number): string {
  return Number.isInteger(n) ? n.toLocaleString('en-US') : String(n);
}

define('caos-usage-bar', CaosUsageBar);

declare global {
  interface HTMLElementTagNameMap {
    'caos-usage-bar': CaosUsageBar;
  }
}
